import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { ArrowRightIcon, ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
type SelfReportedAssessmentsProps = {
  onComplete: () => void;
};
export const SelfReportedAssessments: React.FC<SelfReportedAssessmentsProps> = ({
  onComplete
}) => {
  const [completedAssessments, setCompletedAssessments] = useState<string[]>([]);
  const [activeAssessment, setActiveAssessment] = useState<string | null>(null);
  const [earnedBadges, setEarnedBadges] = useState<string[]>([]);
  const assessments = [{
    id: 'mbti',
    title: 'Personality Type (MBTI)',
    description: 'Discover your psychological preferences in how you perceive the world',
    icon: '🧠',
    color: 'purple',
    badge: 'Personality Master',
    questions: 5 // Simplified for demo
  }, {
    id: 'big-five',
    title: 'Big Five Traits',
    description: 'Measure your openness, conscientiousness, extraversion, agreeableness, and neuroticism',
    icon: '👤',
    color: 'blue',
    badge: 'Self-Aware',
    questions: 5
  }, {
    id: 'holland',
    title: 'Holland Code',
    description: 'Identify your career interests across six different categories',
    icon: '🔍',
    color: 'green',
    badge: 'Interest Explorer',
    questions: 5
  }, {
    id: 'leadership',
    title: 'Leadership Style',
    description: 'Uncover your natural approach to guiding and influencing others',
    icon: '👑',
    color: 'gold',
    badge: 'Leadership Guru',
    questions: 5
  }];
  const completeAssessment = (id: string) => {
    if (!completedAssessments.includes(id)) {
      const assessment = assessments.find(a => a.id === id);
      if (assessment) {
        setCompletedAssessments([...completedAssessments, id]);
        setEarnedBadges([...earnedBadges, assessment.badge]);
        setActiveAssessment(null);
      }
    }
  };
  const allCompleted = completedAssessments.length === assessments.length;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }} className="max-w-4xl mx-auto p-4" data-id="element-1289">
      <div className="mb-8" data-id="element-1290">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2" data-id="element-1291">
          Self-Reported Assessments
        </h1>
        <p className="text-[#B8C0E0]" data-id="element-1292">
          Complete these fun quizzes to reveal your personality traits and
          preferences
        </p>
      </div>
      {activeAssessment ? <AssessmentQuiz assessment={assessments.find(a => a.id === activeAssessment)!} onComplete={() => completeAssessment(activeAssessment)} onBack={() => setActiveAssessment(null)} data-id="element-1293" /> : <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1294">
            {assessments.map(assessment => {
          const isCompleted = completedAssessments.includes(assessment.id);
          return <motion.div key={assessment.id} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => !isCompleted && setActiveAssessment(assessment.id)} data-id="element-1295">
                  <Card className={`cursor-pointer ${isCompleted ? 'border-[#19D3DA]' : ''}`} data-id="element-1296">
                    <div className="flex justify-between items-start mb-4" data-id="element-1297">
                      <div className="text-3xl" data-id="element-1298">{assessment.icon}</div>
                      {isCompleted ? <Badge color="green" data-id="element-1299">
                          <CheckCircleIcon size={16} className="mr-1" data-id="element-1300" />
                          Completed
                        </Badge> : <Badge color={assessment.color as any} data-id="element-1301">
                          {assessment.questions} Questions
                        </Badge>}
                    </div>
                    <h3 className="text-lg font-semibold mb-1" data-id="element-1302">
                      {assessment.title}
                    </h3>
                    <p className="text-[#B8C0E0] text-sm mb-4" data-id="element-1303">
                      {assessment.description}
                    </p>
                    <div className="flex justify-between items-center" data-id="element-1304">
                      <span className="text-sm text-[#B8C0E0]" data-id="element-1305">~5 min</span>
                      <button className={`flex items-center text-sm font-medium rounded-full py-1 px-3 ${isCompleted ? 'bg-[#19D3DA]/20 text-[#19D3DA]' : 'bg-[#7D56F4] text-white'}`} data-id="element-1306">
                        {isCompleted ? 'Retake' : 'Start'}
                        {!isCompleted && <ArrowRightIcon size={14} className="ml-1" data-id="element-1307" />}
                      </button>
                    </div>
                    {isCompleted && <div className="mt-3 pt-3 border-t border-[#3A506B] flex items-center" data-id="element-1308">
                        <div className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] w-6 h-6 rounded-full flex items-center justify-center text-xs" data-id="element-1309">
                          🏆
                        </div>
                        <span className="ml-2 text-sm font-medium text-[#FFD700]" data-id="element-1310">
                          {assessment.badge} Badge Earned!
                        </span>
                      </div>}
                  </Card>
                </motion.div>;
        })}
          </div>
          {earnedBadges.length > 0 && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mt-8 bg-[#1C2541] rounded-lg p-4" data-id="element-1311">
              <h3 className="text-lg font-semibold mb-3" data-id="element-1312">Your Earned Badges</h3>
              <div className="flex flex-wrap gap-3" data-id="element-1313">
                {earnedBadges.map((badge, index) => <div key={index} className="bg-[#0A1128] rounded-lg p-3 flex items-center" data-id="element-1314">
                    <div className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] w-8 h-8 rounded-full flex items-center justify-center text-lg mr-2" data-id="element-1315">
                      🏆
                    </div>
                    <span className="text-sm font-medium" data-id="element-1316">{badge}</span>
                  </div>)}
              </div>
            </motion.div>}
          {allCompleted && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mt-8 flex justify-center" data-id="element-1317">
              <button onClick={onComplete} className="bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-medium py-3 px-8 rounded-full flex items-center" data-id="element-1318">
                <span data-id="element-1319">Continue to Next Level</span>
                <ArrowRightIcon size={18} className="ml-2" data-id="element-1320" />
              </button>
            </motion.div>}
        </>}
    </motion.div>;
};
type AssessmentQuizProps = {
  assessment: {
    id: string;
    title: string;
    color: string;
    icon: string;
  };
  onComplete: () => void;
  onBack: () => void;
};
const AssessmentQuiz: React.FC<AssessmentQuizProps> = ({
  assessment,
  onComplete,
  onBack
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  // Sample questions for demo purposes
  const questions = [{
    text: 'I prefer working in teams rather than independently',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree']
  }, {
    text: 'I enjoy solving complex problems and puzzles',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree']
  }, {
    text: 'I am comfortable taking risks and trying new things',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree']
  }, {
    text: 'I prefer having a structured schedule over flexibility',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree']
  }, {
    text: 'I am energized by social interactions',
    options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree']
  }];
  const handleAnswer = (value: number) => {
    const newAnswers = [...answers, value];
    setAnswers(newAnswers);
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Quiz completed
      setTimeout(() => {
        onComplete();
      }, 1000);
    }
  };
  const progress = (currentQuestion + 1) / questions.length * 100;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} data-id="element-1321">
      <button onClick={onBack} className="mb-4 flex items-center text-[#B8C0E0] hover:text-white" data-id="element-1322">
        <ArrowLeftIcon size={16} className="mr-1" data-id="element-1323" />
        Back to assessments
      </button>
      <Card data-id="element-1324">
        <div className="flex items-center mb-6" data-id="element-1325">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl mr-4 ${assessment.color === 'purple' ? 'bg-purple-500/20' : assessment.color === 'blue' ? 'bg-blue-500/20' : assessment.color === 'green' ? 'bg-green-500/20' : 'bg-yellow-500/20'}`} data-id="element-1326">
            {assessment.icon}
          </div>
          <div data-id="element-1327">
            <h2 className="text-xl font-bold" data-id="element-1328">{assessment.title}</h2>
            <div className="flex items-center mt-1" data-id="element-1329">
              <div className="w-32 h-2 bg-[#1C2541] rounded-full mr-2" data-id="element-1330">
                <div className="h-2 rounded-full bg-[#7D56F4]" style={{
                width: `${progress}%`
              }} data-id="element-1331"></div>
              </div>
              <span className="text-sm text-[#B8C0E0]" data-id="element-1332">
                Question {currentQuestion + 1} of {questions.length}
              </span>
            </div>
          </div>
        </div>
        <motion.div key={currentQuestion} initial={{
        opacity: 0,
        x: 50
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -50
      }} className="mb-6" data-id="element-1333">
          <h3 className="text-lg font-medium mb-4" data-id="element-1334">
            {questions[currentQuestion].text}
          </h3>
          <div className="space-y-3" data-id="element-1335">
            {questions[currentQuestion].options.map((option, index) => <motion.button key={index} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => handleAnswer(index)} className="w-full text-left bg-[#1C2541] hover:bg-[#2C3555] p-4 rounded-lg flex items-center" data-id="element-1336">
                {assessment.id === 'mbti' ? <div className="mr-3 text-xl" data-id="element-1337">
                    {['😔', '🙁', '😐', '🙂', '😊'][index]}
                  </div> : <div className="w-6 h-6 rounded-full border-2 border-[#3A506B] mr-3 flex-shrink-0" data-id="element-1338"></div>}
                <span data-id="element-1339">{option}</span>
              </motion.button>)}
          </div>
        </motion.div>
        <div className="flex justify-between" data-id="element-1340">
          <button onClick={() => currentQuestion > 0 && setCurrentQuestion(currentQuestion - 1)} className={`py-2 px-4 rounded-full ${currentQuestion > 0 ? 'text-[#B8C0E0] hover:text-white' : 'text-[#3A506B] cursor-not-allowed'}`} disabled={currentQuestion === 0} data-id="element-1341">
            <ArrowLeftIcon size={18} data-id="element-1342" />
          </button>
          <div className="text-sm text-[#B8C0E0]" data-id="element-1343">
            {currentQuestion + 1} of {questions.length}
          </div>
          <button onClick={() => currentQuestion < questions.length - 1 && setCurrentQuestion(currentQuestion + 1)} className={`py-2 px-4 rounded-full ${answers[currentQuestion] !== undefined && currentQuestion < questions.length - 1 ? 'text-[#B8C0E0] hover:text-white' : 'text-[#3A506B] cursor-not-allowed'}`} disabled={answers[currentQuestion] === undefined || currentQuestion >= questions.length - 1} data-id="element-1344">
            <ArrowRightIcon size={18} data-id="element-1345" />
          </button>
        </div>
      </Card>
    </motion.div>;
};